import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import useConfig from '../hooks/useConfig';

type SentryOptionsTypes = {
  appName: 'auth' | 'web';
  userId?: string;
  orgId?: string;
};

// Flag to track if the unhandledrejection listener has been added
let unhandledRejectionListenerAdded = false;
let sentryInitialized = false;

const parseTracesSampleRate = (
  value: string | undefined,
): number | undefined => {
  if (!value) {
    return undefined;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
    return undefined;
  }
  return parsed;
};

// Define the handler function outside to enable proper removal
const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
  if (!(event.reason instanceof Error)) {
    event.preventDefault();

    // Check if running locally before sending to Sentry
    // eslint-disable-next-line no-undef
    const domain = globalThis.location.hostname;
    const isLocalhost = domain.includes('localhost') || domain === '127.0.0.1';

    if (!isLocalhost) {
      const normalized = new Error(
        typeof event.reason === 'string' ? event.reason : 'Unhandled rejection',
      );
      Sentry.captureException(normalized, {
        tags: { source: 'unhandledrejection' },
      });
    }
  }
};

export const useInitializeSentry = ({
  appName,
  userId,
  orgId,
}: Readonly<SentryOptionsTypes>) => {
  const domain = window.location.hostname;
  const { SENTRY_DSN, APP_VERSION, ENVIRONMENT, SENTRY_TRACES_SAMPLE_RATE } =
    useConfig();

  const isLocalhost = domain.includes('localhost') || domain === '127.0.0.1';
  const environment =
    ENVIRONMENT ||
    (isLocalhost ? 'development' : process.env.NODE_ENV) ||
    'unknown';
  let defaultTracesSampleRate = 1.0;
  if (isLocalhost) {
    defaultTracesSampleRate = 0;
  } else if (environment === 'production') {
    defaultTracesSampleRate = 0.2;
  }
  const tracesSampleRate =
    parseTracesSampleRate(SENTRY_TRACES_SAMPLE_RATE) ?? defaultTracesSampleRate;
  const release = `${appName}@${APP_VERSION || 'unknown'}`;

  useEffect(() => {
    if (!SENTRY_DSN || sentryInitialized) {
      return;
    }

    // Add unhandled rejection handler before Sentry.init (only once)
    if (!unhandledRejectionListenerAdded) {
      window.addEventListener('unhandledrejection', handleUnhandledRejection);
      unhandledRejectionListenerAdded = true;
    }

    Sentry.init({
      dsn: SENTRY_DSN,
      integrations: [Sentry.browserTracingIntegration()],
      // Explicitly disable default PII capture; allowlist context deliberately via tags/user id.
      sendDefaultPii: false,
      tracesSampleRate,
      environment,
      release,
      serverName: domain,
      initialScope: {
        tags: {
          app: appName,
          domain,
        },
      },
      beforeSend(event) {
        if (isLocalhost) {
          return null;
        }
        return event;
      },
    });
    sentryInitialized = true;
  }, [
    SENTRY_DSN,
    appName,
    domain,
    environment,
    isLocalhost,
    release,
    tracesSampleRate,
  ]);

  useEffect(() => {
    Sentry.setUser(userId ? { id: userId } : null);
  }, [userId]);

  useEffect(() => {
    // Keep org context explicit to avoid stale attribution after org switch/logout.
    Sentry.setTag('orgId', orgId || 'none');
  }, [orgId]);
};
